import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, VideoIcon, FileEdit, Trash2, PackageOpen, Upload, Search, CheckSquare, Square, X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import VideoCard from './VideoCard';
import WBCollectionsModal from './WBCollectionsModal';
import WBEditDecisionModal from './WBEditDecisionModal';
import { useVideos, broadcastVideosRefresh } from '../hooks/useVideos';
import { useAuth } from '../hooks/useAuth';
import { useChannel } from '../hooks/useChannel';
import type { VideoEditDraft } from '../types/videoEditDraft';
import { loadDrafts, deleteVideoEditDraft, subscribeDraftsRefresh } from '../lib/videoEditDraftStorage';
import type { Video } from '../types/video';
import { toast } from '../lib/toast';
import { ConfirmDialog } from './ConfirmDialog';
import UploadVideoDialog, { MAX_UPLOAD_BYTES, uploadType } from './UploadVideoDialog';
import { BIN_RETENTION_DAYS, toastMovedToBin } from '../hooks/useRecycleBin';
import { richTextToPlain } from '../lib/richText';
import { useReadOnly } from '../hooks/useArchiveAccess';

type SortKey = 'newest' | 'oldest' | 'name' | 'size' | 'duration';
const SORTS: SortKey[] = ['newest', 'oldest', 'name', 'size', 'duration'];
const SORT_LABELS: Record<SortKey, string> = {
  newest: 'Newest first', oldest: 'Oldest first', name: 'Name (A–Z)', size: 'Largest first', duration: 'Longest first',
};
const SORT_KEY = 'airec.library.sort';
/** Lower case without accents, so "video" finds "vídeo". */
const fold = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const controlStyle: React.CSSProperties = {
  backgroundColor: 'var(--dropdown-bg, var(--card-bg, var(--bg-primary, #ffffff)))',
  color: 'var(--dropdown-foreground, var(--text-primary, var(--theme-foreground, #1a1a1a)))',
  border: '1px solid var(--border-color, #d6d6d6)',
  borderRadius: 6,
};

// Cards fill the width the page really has (with or without the side menu), 260px minimum.
const GRID: React.CSSProperties = { display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))' };

interface VideoLibraryProps {
  onOpenRecording: () => void;
  onEdit?: (video: import('../types/video').Video) => void;
  onOpenDraft?: (draft: VideoEditDraft) => void;
  /** Search typed in the top bar (fills the library search). */
  searchQuery?: string;
}

const VideoLibrary: React.FC<VideoLibraryProps> = ({ onOpenRecording, onEdit, onOpenDraft, searchQuery }) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('videos');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [drafts, setDrafts] = useState<VideoEditDraft[]>([]);
  const { videos, loading, loadFailed, fetchVideos, deleteVideo, updateVideoInList } = useVideos();
  const { user } = useAuth();
  const readOnly = useReadOnly(); // Admin Center Archive Mode: watching only
  const { channels: myChannels } = useChannel();

  // Whiteboard Collections import flow
  const [showWBModal, setShowWBModal] = useState(false);

  // Upload a video file (button or drop on the library).
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const pickUpload = (file?: File | null) => {
    if (!file || uploadFile) return;
    if (!uploadType(file)) {
      toast.error(t('video_upload.bad_type', 'Choose an MP4, WebM or MOV video.'));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t('video_upload.too_large', 'The video is larger than 2 GB.'));
      return;
    }
    setUploadFile(file);
  };
  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const [wbEditVideo, setWbEditVideo] = useState<Video | null>(null);

  useEffect(() => {
    if (!user?.email) {
      setDrafts([]);
      return;
    }
    const slug = localStorage.getItem('slug') || 'default';
    const refresh = () => setDrafts(loadDrafts(user.email, slug));
    refresh();
    return subscribeDraftsRefresh(refresh);
  }, [user?.email]);

  // Search (title and description) and sort; the sort is remembered per browser.
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>(() => {
    try {
      const saved = localStorage.getItem(SORT_KEY) as SortKey | null;
      return saved && SORTS.includes(saved) ? saved : 'newest';
    } catch { return 'newest'; }
  });
  useEffect(() => {
    if (searchQuery === undefined) return;
    setQuery(searchQuery);
    if (searchQuery.trim()) setSelectedTab('videos');
  }, [searchQuery]);
  const changeSort = (value: SortKey) => {
    setSort(value);
    try { localStorage.setItem(SORT_KEY, value); } catch { /* not remembered */ }
  };

  const filteredVideos = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    const list = videos.filter(video => {
      const d = video.duration ?? 0;
      if (durationFilter === 'short' && !(d < 60)) return false;
      if (durationFilter === 'medium' && !(d >= 60 && d < 300)) return false;
      if (durationFilter === 'long' && !(d >= 300)) return false;
      if (!words.length) return true;
      const text = fold(`${video.title || ''} ${richTextToPlain(video.description || '')}`);
      return words.every(w => text.includes(w));
    });
    const created = (v: Video) => Date.parse(v.created_at || v.createdAt || '') || 0;
    const size = (v: Video) => v.fileSize || v.file_size || 0;
    const compare: Record<SortKey, (a: Video, b: Video) => number> = {
      newest: (a, b) => created(b) - created(a),
      oldest: (a, b) => created(a) - created(b),
      name: (a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base', numeric: true }),
      size: (a, b) => size(b) - size(a),
      duration: (a, b) => (b.duration ?? 0) - (a.duration ?? 0),
    };
    return [...list].sort(compare[sort]);
  }, [videos, durationFilter, query, sort]);

  // Select mode: tick videos, then delete them together.
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // Only videos still shown count (a refresh or the search may have removed some).
  const selectedVideos = filteredVideos.filter(v => selected.has(v.id));
  const allShownSelected = filteredVideos.length > 0 && selectedVideos.length === filteredVideos.length;
  const stopSelecting = () => { setSelecting(false); setSelected(new Set()); };
  const toggleSelected = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    const targets = selectedVideos;
    const failed: Video[] = [];
    setBulkProgress({ done: 0, total: targets.length });
    // One at a time: each delete releases its own storage.
    for (const [i, v] of targets.entries()) {
      if (!(await deleteVideo(v.id, { refresh: false }))) failed.push(v);
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
    setConfirmBulkDelete(false);
    broadcastVideosRefresh();
    toastMovedToBin(targets.filter(v => !failed.includes(v)).map(v => v.id));
    if (failed.length) {
      toast.error(t('library_select.delete_failed', 'Could not move: {{titles}}', {
        titles: failed.slice(0, 3).map(v => v.title || v.id).join(', ') + (failed.length > 3 ? '…' : ''),
        interpolation: { escapeValue: false },
      }));
      // The failed ones stay selected for another try.
      setSelected(new Set(failed.map(v => v.id)));
    } else {
      stopSelecting();
    }
  };

  const tabs = [
    { id: 'videos', label: t('videos_title') },
    { id: 'drafts', label: t('library_tab_drafts') },
  ];

  /** Intercepts edit calls for WB-imported videos to show the decision modal first. */
  const handleEdit = useCallback((video: Video) => {
    // Only a video still pointing at the Whiteboard file; a copy is edited like any other video.
    if (video.source === 'whiteboard-collections') {
      setWbEditVideo(video);
      return;
    }
    onEdit?.(video);
  }, [onEdit]);

  /** After WB blob copy is done, update the list and proceed to the AIRecording editor. */
  const handleWbCopyReady = useCallback((copiedVideo: Video) => {
    updateVideoInList?.(copiedVideo.id, copiedVideo);
    onEdit?.(copiedVideo);
    setWbEditVideo(null);
  }, [onEdit, updateVideoInList]);

  // Draft deletion waits for confirmation in an in-app dialog.
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  const handleDeleteDraft = (id: string) => {
    if (!user?.email) return;
    setDraftToDelete(id);
  };
  const confirmDeleteDraft = () => {
    if (!user?.email || !draftToDelete) return;
    const slug = localStorage.getItem('slug') || 'default';
    deleteVideoEditDraft(user.email, slug, draftToDelete);
    setDraftToDelete(null);
    toast.success(t('draft_deleted', 'Draft deleted'));
  };

  if (!user) {
    return (
      <div className="flex-1 p-6" style={{ backgroundColor: 'var(--bg-primary, var(--theme-background, #f9fafb))' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <VideoIcon className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary, #9ca3af)' }} />
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
                {t('sign_in_to_view_videos')}
              </h2>
              <p style={{ color: 'var(--text-secondary, #6b7280)' }}>
                {t('create_account_to_start')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      className="flex-1 p-6"
      style={{
        backgroundColor: 'var(--bg-primary, var(--theme-background, #f9fafb))',
        ...(dragging ? { outline: '2px dashed var(--theme-primary, #1976d2)', outlineOffset: -8 } : {}),
      }}
      onDragOver={(e) => { if (!isFileDrag(e) || readOnly) return; e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
      onDrop={(e) => {
        if (!isFileDrag(e) || readOnly) return;
        e.preventDefault();
        setDragging(false);
        pickUpload(e.dataTransfer.files[0]);
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
            {t('my_library_title')}
          </h1>
          {/* Upload a video file; import from Whiteboard Collections (the user's own Collections videos). */}
          {user && !readOnly && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                background: 'var(--controlsBg)', color: 'var(--controlsForeground)',
                border: '1px solid var(--controlsBorder)', cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              title={t('video_upload.button_hint', 'Add an MP4, WebM or MOV video from your computer (or drop it here)')}
            >
              <Upload size={16} aria-hidden />
              {t('video_upload.button', 'Upload video')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              hidden
              onChange={(e) => { pickUpload(e.target.files?.[0]); e.target.value = ''; }}
            />
            <button
              type="button"
              onClick={() => setShowWBModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                background: 'var(--controlsBg)', color: 'var(--controlsForeground)',
                border: '1px solid var(--controlsBorder)', cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              title={t('wb_import.button')}
            >
              <PackageOpen size={16} aria-hidden />
              {t('wb_import.button')}
            </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div
            className="flex border-b"
            style={{ borderColor: 'var(--border-color, var(--theme-border, #d6d6d6))' }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setSelectedTab(tab.id); stopSelecting(); }}
                className="px-4 py-2 font-medium text-sm border-b-2 transition-colors"
                style={{
                  borderColor:
                    selectedTab === tab.id
                      ? 'var(--dropdown-item-selected-bg, var(--button-primary-bg, var(--theme-primary, #4f46e5)))'
                      : 'transparent',
                  backgroundColor:
                    selectedTab === tab.id
                      ? 'var(--dropdown-item-selected-bg, var(--button-primary-bg, var(--theme-primary, #4f46e5)))'
                      : 'transparent',
                  color:
                    selectedTab === tab.id
                      ? 'var(--dropdown-item-selected-foreground, var(--button-primary-foreground, #ffffff))'
                      : 'var(--text-primary, var(--theme-foreground, #111827))',
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div
            className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm"
            style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))', opacity: 0.92 }}
          >
            {selectedTab === 'videos' ? (
              <>
                <div className="flex items-center gap-2 relative">
                  <Search size={14} className="absolute left-2 pointer-events-none" style={{ opacity: 0.6 }} aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('library_search.placeholder', 'Search videos')}
                    aria-label={t('library_search.placeholder', 'Search videos')}
                    className="text-sm"
                    style={{ ...controlStyle, padding: '3px 8px 3px 26px', width: 'min(180px, 60vw)' }}
                  />
                </div>
                <span className="whitespace-nowrap">{filteredVideos.length} {t('videos_count')}</span>
                <div className="flex items-center gap-2 relative">
                  <select
                    value={sort}
                    onChange={(e) => changeSort(e.target.value as SortKey)}
                    className="text-sm focus:ring-0 focus:ring-offset-0 cursor-pointer"
                    aria-label={t('library_sort.label', 'Sort')}
                    style={{ ...controlStyle, padding: '3px 28px 3px 8px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                  >
                    {SORTS.map(key => (
                      <option key={key} value={key}>{t(`library_sort.${key}`, SORT_LABELS[key])}</option>
                    ))}
                  </select>
                  <ChevronDown
                    className="w-4 h-4 pointer-events-none absolute right-2"
                    style={{ color: 'var(--dropdown-foreground, var(--text-primary, #111827))' }}
                    aria-hidden="true"
                  />
                </div>
                <div className="flex items-center gap-2 relative">
                  <select
                    value={durationFilter}
                    onChange={(e) => setDurationFilter(e.target.value)}
                    className="text-sm focus:ring-0 focus:ring-offset-0 cursor-pointer"
                    aria-label={t('all_durations')}
                    style={{
                      backgroundColor: 'var(--dropdown-bg, var(--card-bg, var(--bg-primary, #ffffff)))',
                      color: 'var(--dropdown-foreground, var(--text-primary, var(--theme-foreground, #1a1a1a)))',
                      border: '1px solid var(--border-color, #d6d6d6)',
                      borderRadius: '6px',
                      padding: '3px 28px 3px 8px',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                    }}
                  >
                    <option value="all">{t('all_durations')}</option>
                    <option value="short">{t('short_duration')}</option>
                    <option value="medium">{t('medium_duration')}</option>
                    <option value="long">{t('long_duration')}</option>
                  </select>
                  <ChevronDown
                    className="w-4 h-4 pointer-events-none absolute right-2"
                    style={{ color: 'var(--dropdown-foreground, var(--text-primary, #111827))' }}
                    aria-hidden="true"
                  />
                </div>
                {videos.length > 0 && !readOnly && (
                  <button
                    type="button"
                    onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
                    className="text-sm flex items-center gap-1"
                    style={{ ...controlStyle, padding: '3px 10px', cursor: 'pointer' }}
                    aria-pressed={selecting}
                  >
                    <CheckSquare size={14} aria-hidden />
                    {selecting ? t('library_select.done', 'Done') : t('library_select.select', 'Select')}
                  </button>
                )}
              </>
            ) : (
              <span>{drafts.length} {t('drafts_count_label')}</span>
            )}
          </div>
        </div>

        {selectedTab === 'drafts' ? (
          drafts.length === 0 ? (
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center max-w-md">
                <FileEdit className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary, #9ca3af)' }} aria-hidden />
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
                  {t('drafts_empty_title')}
                </h2>
                <p style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  {t('drafts_empty_hint')}
                </p>
              </div>
            </div>
          ) : (
            <div style={GRID}>
              {drafts.map((draft) => (
                <div
                  key={draft.id}
                  className="rounded-lg p-4 flex flex-col gap-3"
                  style={{
                    backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
                    border: '1px solid var(--border-color, var(--theme-border, #d6d6d6))',
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm line-clamp-2" style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
                      {draft.title}
                    </h3>
                    <button
                      type="button"
                      className="p-1 rounded shrink-0"
                      style={{ color: 'var(--text-secondary, #6b7280)' }}
                      onClick={() => handleDeleteDraft(draft.id)}
                      aria-label={t('draft_delete')}
                    >
                      <Trash2 size={16} aria-hidden />
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                    {t('draft_card_source')}: {draft.source.title}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                    {t('draft_card_updated')}: {new Date(draft.updatedAt).toLocaleString()}
                  </p>
                  <button
                    type="button"
                    className="mt-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: 'var(--button-primary-bg, var(--theme-primary, #4f46e5))',
                      color: 'var(--button-primary-foreground, #ffffff)',
                      border: 'var(--button-primary-border, 1px solid transparent)',
                    }}
                    onClick={() => onOpenDraft?.(draft)}
                  >
                    {t('draft_continue_editing')}
                  </button>
                </div>
              ))}
            </div>
          )
        ) : loading ? (
          <div style={GRID}>
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="rounded-lg h-64 animate-pulse"
                style={{
                  backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
                  border: '1px solid var(--border-color, var(--theme-border, #d6d6d6))',
                }}
              />
            ))}
          </div>
        ) : loadFailed && videos.length === 0 ? (
          // Not an empty library: the list could not be loaded.
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-secondary, #9ca3af)' }} aria-hidden />
              <p className="mb-4" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                {t('library_load.failed', 'Your videos could not be loaded. Check your connection and try again.')}
              </p>
              <button
                type="button"
                onClick={() => void fetchVideos()}
                className="px-6 py-2 rounded-lg"
                style={{
                  backgroundColor: 'var(--button-primary-bg, var(--theme-primary, #4f46e5))',
                  color: 'var(--button-primary-foreground, #ffffff)',
                  border: 'var(--button-primary-border, 1px solid transparent)',
                }}
              >
                {t('library_load.retry', 'Try again')}
              </button>
            </div>
          </div>
        ) : filteredVideos.length === 0 && videos.length > 0 ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-secondary, #9ca3af)' }} aria-hidden />
              <p style={{ color: 'var(--text-secondary, #6b7280)' }}>
                {t('library_search.no_results', 'No videos match your search or filter.')}
              </p>
            </div>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <VideoIcon className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary, #9ca3af)' }} />
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
                {t('no_videos_yet')}
              </h2>
              <p className="mb-6" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                {t('start_by_recording')}
              </p>
              <button
                type="button"
                onClick={onOpenRecording}
                className="px-6 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--button-primary-bg, var(--theme-primary, #4f46e5))',
                  color: 'var(--button-primary-foreground, #ffffff)',
                  border: 'var(--button-primary-border, 1px solid transparent)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--button-primary-hover-bg, var(--theme-primary, #4338ca))';
                  e.currentTarget.style.border = 'var(--button-primary-hover-border, var(--button-primary-border, 1px solid transparent))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--button-primary-bg, var(--theme-primary, #4f46e5))';
                  e.currentTarget.style.border = 'var(--button-primary-border, 1px solid transparent)';
                }}
              >
                {t('record_video_button')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {selecting && (
              <div
                className="flex items-center gap-3 flex-wrap mb-4 text-sm"
                style={{
                  ...controlStyle, padding: '8px 12px', borderRadius: 8,
                  position: 'sticky', top: 8, zIndex: 20,
                }}
              >
                <strong>{t('library_select.count', '{{count}} selected', { count: selectedVideos.length })}</strong>
                <button
                  type="button"
                  className="flex items-center gap-1"
                  onClick={() => setSelected(allShownSelected ? new Set() : new Set(filteredVideos.map(v => v.id)))}
                >
                  {allShownSelected ? <Square size={14} aria-hidden /> : <CheckSquare size={14} aria-hidden />}
                  {allShownSelected ? t('library_select.none', 'Select none') : t('library_select.all', 'Select all')}
                </button>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  disabled={!selectedVideos.length}
                  onClick={() => setConfirmBulkDelete(true)}
                  className="flex items-center gap-1"
                  style={{
                    padding: '5px 12px', borderRadius: 6, border: 'none', fontWeight: 600,
                    background: '#dc2626', color: '#fff',
                    opacity: selectedVideos.length ? 1 : 0.5, cursor: selectedVideos.length ? 'pointer' : 'default',
                  }}
                >
                  <Trash2 size={14} aria-hidden />
                  {t('library_select.delete', 'Move to Recycle bin')}
                </button>
                <button type="button" className="flex items-center gap-1" onClick={stopSelecting}>
                  <X size={14} aria-hidden />
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            )}
            <div style={GRID}>
              {filteredVideos.map((video) => (
                <div key={video.id} className="relative">
                  <VideoCard
                    video={video}
                    onDelete={deleteVideo}
                    onEdit={handleEdit}
                    myChannels={myChannels}
                    onChannelAssigned={(videoId, channelId) => {
                      updateVideoInList?.(videoId, { channelId: channelId ?? undefined });
                    }}
                  />
                  {/* Select mode: a click on the card selects it (nothing else opens). */}
                  {selecting && (
                    <button
                      type="button"
                      onClick={() => toggleSelected(video.id)}
                      aria-pressed={selected.has(video.id)}
                      aria-label={video.title}
                      className="absolute inset-0 rounded-lg"
                      style={{
                        zIndex: 15, cursor: 'pointer', background: selected.has(video.id) ? 'rgba(25,118,210,0.12)' : 'transparent',
                        border: `2px solid ${selected.has(video.id) ? 'var(--theme-primary, #1976d2)' : 'transparent'}`,
                      }}
                    >
                      <span
                        className="absolute flex items-center justify-center rounded"
                        style={{
                          top: 10, left: 10, width: 22, height: 22,
                          background: selected.has(video.id) ? 'var(--theme-primary, #1976d2)' : 'rgba(255,255,255,0.92)',
                          border: '2px solid var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
                        }}
                        aria-hidden
                      >
                        {selected.has(video.id) && <CheckSquare size={14} />}
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Whiteboard Collections import modal */}
    {showWBModal && (
      <WBCollectionsModal
        onClose={() => setShowWBModal(false)}
        onImported={() => {
          // New item: refetch the list (updateVideoInList only replaces existing ones).
          broadcastVideosRefresh();
        }}
      />
    )}

    {/* WB edit decision modal */}
    {wbEditVideo && (
      <WBEditDecisionModal
        video={wbEditVideo}
        onClose={() => setWbEditVideo(null)}
        onEditCopy={handleWbCopyReady}
      />
    )}

    {draftToDelete && (
      <ConfirmDialog
        danger
        title={t('draft_delete_confirm')}
        message={t('draft_delete_confirm_message', "The draft will be removed from this browser. This can't be undone.")}
        confirmLabel={t('draft_delete')}
        onConfirm={confirmDeleteDraft}
        onCancel={() => setDraftToDelete(null)}
      />
    )}
    {uploadFile && <UploadVideoDialog file={uploadFile} onClose={() => setUploadFile(null)} />}
    {confirmBulkDelete && (
      <ConfirmDialog
        danger
        title={t('library_select.confirm_title', 'Move {{count}} videos to the Recycle bin?', { count: selectedVideos.length })}
        message={
          <>
            {t('library_select.to_bin', 'The selected videos move to the Recycle bin. You can restore them within {{days}} days.', { days: BIN_RETENTION_DAYS })}
            {bulkProgress && (
              <span style={{ display: 'block', marginTop: 6 }}>
                {t('library_select.progress', 'Moving {{done}} of {{total}}…', { done: bulkProgress.done, total: bulkProgress.total })}
              </span>
            )}
          </>
        }
        confirmLabel={t('library_select.delete', 'Move to Recycle bin')}
        onConfirm={bulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
      />
    )}
    </>
  );
};

export default VideoLibrary;
